import { useEffect, useState, useCallback } from 'react';
import { useApiClient } from './useApiClient';

/**
 * Tier-bearing evaluators (currently F2; F1/R1 in v1.3+) populate this block
 * to expose the element-list maturity rubric. The UI shows the achieved tier
 * as a badge and renders the per-element ✓/✗ grid in the evidence panel.
 *
 * Score conversion: pillar aggregation uses tier_achieved/tier_max as the
 * fractional verdict. Below-Tier-1 maps to 0; Tier 3 of 3 maps to 1.0.
 */
export interface MaturityBreakdown {
  tier_achieved: number;        // 0 (below tier 1) | 1 | 2 | 3
  tier_max: number;             // 3 for F2
  elements_present: string[];
  elements_missing: string[];
  tier_definitions: Array<{
    tier: number;
    label: string;              // e.g. 'Citation'
    required_elements: string[];
  }>;
}

export interface EvidencePayload {
  method: string;
  signals: Array<{ type: string; location: string; value: string; role: string }>;
  threshold?: string;
  measured?: string;
  fallbacks_tried?: Array<{ method: string; outcome: string }>;
  shacl_shape_link?: { shape_name: string; snippet?: string; source_path?: string };
  /** Tier-bearing rules only (F2 today). Absent for binary rules. */
  maturity_breakdown?: MaturityBreakdown;
  human_decision?: {
    action: 'accept' | 'reject' | 'edit';
    scope: { type: string; id: string | null; natural_key: string | null; display_name: string };
    rationale: string | null;
    decided_by: { id: string; display_name: string };
    decided_at: string;
    stale: boolean;
    machine_verdict_at_decision_time: 'PASS' | 'FAIL' | 'NOT_APPLICABLE' | null;
    engine_version_at_decision_time: string;
    /**
     * Present only when the human's decision targets a tier-bearing rule.
     * `asserted_tier === 0` means "below Tier 1" (Reject downgrade).
     */
    maturity_decision?: {
      asserted_tier: number;
      machine_tier_at_decision_time: number | null;
    } | null;
  };
  machine_verdict_preserved?: {
    status: 'PASS' | 'FAIL' | 'NOT_APPLICABLE';
    method: string;
    why_sentence: string;
    maturity_tier?: number | null;
  };
}

export interface FairRuleResult {
  rule: string;
  ruleName: string;
  pillar: string;
  status: 'PASS' | 'FAIL' | 'NOT_APPLICABLE';
  evaluatedBy: 'shacl' | 'supplementary' | 'none' | 'human' | 'human_with_machine_concur';
  description: string;
  evidence: string;
  evidence_structured: EvidencePayload;
}

export interface EvaluateData {
  job_id: string;
  rules: FairRuleResult[];
  pillar_scores: { F: number; A: number; I: number; R: number; overall: number };
  review_thoroughness: { accepted: number; rejected: number; edited: number; pending: number; stale: number };
  engine_version: string;
  tier_weights: { foundational: number; core: number; maturity: number };
  scope_context: {
    dataset_natural_key: string | null;
    schema_natural_key: string | null;
    catalog_natural_key: string | null;
  };
}

export function useEvaluateData(jobId: string | undefined) {
  const { authFetch } = useApiClient();
  const [data, setData] = useState<EvaluateData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await authFetch(`/api/analysis/${jobId}/evaluate`);
      if (!r.ok) {
        const errText = await r.text();
        throw new Error(`HTTP ${r.status}: ${errText}`);
      }
      setData(await r.json() as EvaluateData);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [jobId, authFetch]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}
